import { VStack, IconButton, ButtonGroup, useColorModeValue } from '@ecoinvent/ui';
import { FiMaximize2, FiMinimize2, FiMinus } from 'react-icons/fi';
import { MdAdd } from 'react-icons/md';

type ControlsProps = {
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
};

const handleKeyDown = (
  e: React.KeyboardEvent,
  callback: () => void,
  allowedKeys: string[] = ['Enter', ' '],
  blockModifiers = false
) => {
  const hasModifiers = e.ctrlKey || e.metaKey || e.altKey || e.shiftKey;

  if (allowedKeys.includes(e.key) && (!blockModifiers || !hasModifiers)) {
    e.preventDefault();
    callback();
  }
};

export const Controls = ({ isFullScreen, onToggleFullScreen, onZoomIn, onZoomOut }: ControlsProps) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const iconColor = useColorModeValue('gray.700', 'gray.100');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');

  return (
    <VStack spacing={2} alignItems="flex-end" pointerEvents="auto">
      <IconButton
        aria-label={isFullScreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        icon={isFullScreen ? <FiMinimize2 size={16} /> : <FiMaximize2 size={16} />}
        onClick={onToggleFullScreen}
        onKeyDown={(e) => handleKeyDown(e, onToggleFullScreen)}
        size="sm"
        bg={bgColor}
        color={iconColor}
        _hover={{ bg: hoverBg }}
        borderRadius="md"
        boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.1)"
      />
      <ButtonGroup size="sm" isAttached orientation="vertical">
        <IconButton
          aria-label="Zoom in (Plus key)"
          icon={<MdAdd size={18} />}
          onClick={onZoomIn}
          onKeyDown={(e) => handleKeyDown(e, onZoomIn, ['Enter', ' ', '+', '='], true)}
          bg={bgColor}
          color={iconColor}
          _hover={{ bg: hoverBg }}
          borderRadius="md"
          boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.1)"
        />
        <IconButton
          aria-label="Zoom out (Minus key)"
          icon={<FiMinus size={18} />}
          onClick={onZoomOut}
          onKeyDown={(e) => handleKeyDown(e, onZoomOut, ['Enter', ' ', '-', '_'], true)}
          bg={bgColor}
          color={iconColor}
          _hover={{ bg: hoverBg }}
          borderRadius="md"
          boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.1)"
        />
      </ButtonGroup>
    </VStack>
  );
};
